import {
  dispatchEvent,
  ElementIntersectionObserver,
  setAttribute,
  toArray
} from '@nrk/dh-basis-common'

/* Attributes */
const VIEWPORT_POSITION_ATTR = 'viewport-position'
const ACTIVE_BACKGROUND_ATTR = 'active-background-id'
const ACTIVE_LAYER_IDS_ATTR = 'active-layer-ids'

/* Child attributes */
const BG_ID_ATTR = 'data-background-id'
const SHOW_BACKGROUND_ATTR = 'data-show-background'
const LAYER_ID_ATTR = 'data-layer-id'
const SHOW_LAYERS_ATTR = 'data-show-layers'
const ATTRIBUTE_NAME = 'data-attribute-name'
const ATTRIBUTE_VALUE = 'data-attribute-value'
const STATE_ATTR = 'data-state'
const VIEWPORT_POSITION_DATA_ATTR = 'data-viewport-position'
const POSITIONS = {
  aboveViewport: 'ABOVE-VIEWPORT',
  inViewport: 'IN-VIEWPORT',
  belowViewport: 'BELOW-VIEWPORT'
}

function isTrigger(elm) {
  return elm.hasAttribute(SHOW_BACKGROUND_ATTR)
}

function isBackground(elm) {
  return elm.hasAttribute(BG_ID_ATTR)
}

function isLayer(elm) {
  return elm.hasAttribute(LAYER_ID_ATTR)
}

export default class Scrollhistorie extends HTMLElement {
  _position = POSITIONS.aboveViewport
  _activeTriggerIndex

  static get observedAttributes() {
    return ['root-margin', ACTIVE_BACKGROUND_ATTR, ACTIVE_LAYER_IDS_ATTR, VIEWPORT_POSITION_ATTR]
  }

  connectedCallback() {
    this._updateBackgrounds()
    this._updateSizes()
    window.addEventListener('resize', this._updateSizes)
    this._createIntersectionObserver()
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._updateSizes)
    this._disconnectIntersectionObserver()
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'root-margin') {
      this._disconnectIntersectionObserver()
      this._createIntersectionObserver()
    } else if (name === VIEWPORT_POSITION_ATTR) {
      this._updateBackgrounds()
    } else if (name === ACTIVE_BACKGROUND_ATTR) {
      this._updateBackgrounds()
    } else if (name === ACTIVE_LAYER_IDS_ATTR) {
      this._updateLayers()
    }
  }

  _updateSizes = () => {
    const { width } = this.getBoundingClientRect()
    this.style.setProperty('--dh-scrollhistorie-width', `${width}px`)
  }

  _createIntersectionObserver() {
    this.containerObserver = new ElementIntersectionObserver([this], this._handleContainerChange, {
      threshold: [0, 0.1, 0.5, 0.9, 1]
    })
    this.observer = new ElementIntersectionObserver(
      this.triggerElements,
      this._handleTriggerChange,
      {
        rootMargin: this.rootMargin
      }
    )
  }

  _disconnectIntersectionObserver() {
    if (this.containerObserver) {
      this.containerObserver.disconnect()
    }
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  _handleContainerChange = ([containerIntersection]) => {
    if (containerIntersection.isIntersecting) {
      if (this.viewportPosition !== POSITIONS.inViewport) {
        this.viewportPosition = POSITIONS.inViewport
      }
    } else {
      if (this.viewportPosition === POSITIONS.inViewport) {
        const bottom = this.getBoundingClientRect().bottom
        const isAbove = bottom <= 0
        this.viewportPosition = isAbove ? POSITIONS.aboveViewport : POSITIONS.belowViewport

        // Prepare background
        const triggerElements = this.triggerElements
        const triggerIndex = isAbove ? triggerElements.length - 1 : 0
        this.activeBackgroundId = triggerElements[triggerIndex].getAttribute(SHOW_BACKGROUND_ATTR)
      }
    }
  }

  _handleTriggerChange = intersections => {
    const activeIntersection = intersections.filter(i => i.isIntersecting && i.ratio > 0).pop()

    if (!activeIntersection || !activeIntersection.target) {
      return
    }

    const triggerElement = activeIntersection.target

    this.activeBackgroundId = triggerElement.getAttribute(SHOW_BACKGROUND_ATTR)
    const attributeName = triggerElement.getAttribute(ATTRIBUTE_NAME)
    const attributeValue = triggerElement.getAttribute(ATTRIBUTE_VALUE)

    const activeBackground = this.activeBackground
    if (activeBackground && attributeName) {
      this.activeBackground.setAttribute(attributeName, attributeValue)
    }

    this.triggerElements.forEach(elm =>
      setAttribute(elm, STATE_ATTR, elm === triggerElement ? 'ACTIVE' : undefined)
    )
    this.activeLayerIds = triggerElement.getAttribute(SHOW_LAYERS_ATTR)

    const triggerIndex = this.triggerElements.indexOf(triggerElement)
    if (triggerIndex !== this._activeTriggerIndex) {
      this._activeTriggerIndex = triggerIndex
      dispatchEvent(this, 'dh-basis-scrollhistorie-trigger-activated', {
        triggerIndex,
        backgroundId: this.activeBackgroundId,
        attributeName,
        attributeValue
      })
    }
  }

  _updateBackgrounds() {
    const activeBackgroundId = this.activeBackgroundId

    const backgrounds = this.backgrounds
    backgrounds.forEach((background, idx) => {
      const backgroundId = background.getAttribute(BG_ID_ATTR)
      const isActive = backgroundId === activeBackgroundId
      const nextActive = this._getBackgroundId(backgrounds, idx + 1) === activeBackgroundId
      const prevActive = this._getBackgroundId(backgrounds, idx - 1) === activeBackgroundId

      setAttribute(background, VIEWPORT_POSITION_DATA_ATTR, this.viewportPosition)

      let state
      if (isActive) {
        state = 'ACTIVE'
      } else if (nextActive) {
        state = 'PREV'
      } else if (prevActive) {
        state = 'NEXT'
      }

      const currentState = background.getAttribute(STATE_ATTR)
      if (state !== currentState) {
        setAttribute(background, STATE_ATTR, state)
      }
    })
  }

  _getBackgroundId(backgrounds, index) {
    if (backgrounds[index]) {
      return backgrounds[index].getAttribute(BG_ID_ATTR)
    }
  }

  _updateLayers() {
    const activeLayerIds = this.activeLayerIds.replace(/[[\]]/g, '').split(',')
    this.layers.forEach(layer => {
      const layerId = layer.getAttribute(LAYER_ID_ATTR)
      const isActive = activeLayerIds.indexOf(layerId) !== -1
      setAttribute(layer, STATE_ATTR, isActive ? 'ACTIVE' : undefined)
    })
  }

  get triggerElements() {
    return toArray(this.children).filter(isTrigger)
  }

  get backgrounds() {
    return toArray(this.children).filter(isBackground)
  }

  get layers() {
    return toArray(this.children).filter(isLayer)
  }

  /* Mirrored attributes: */

  get rootMargin() {
    return this.getAttribute('root-margin') || '0px 0px 0px 0px'
  }

  set rootMargin(val) {
    this.setAttribute('root-margin', val || '0px 0px 0px 0px')
  }

  set viewportPosition(value) {
    if (value !== this.viewportPosition) {
      this.setAttribute(VIEWPORT_POSITION_ATTR, value)
    }
  }

  get viewportPosition() {
    return this.getAttribute(VIEWPORT_POSITION_ATTR)
  }

  set activeBackgroundId(value) {
    if (value !== this.activeBackgroundId) {
      this.setAttribute(ACTIVE_BACKGROUND_ATTR, value)
    }
  }

  get activeBackgroundId() {
    return this.getAttribute(ACTIVE_BACKGROUND_ATTR)
  }

  get activeBackground() {
    return this.querySelector(`[${BG_ID_ATTR}="${this.activeBackgroundId}"]`)
  }

  get activeLayerIds() {
    return this.getAttribute(ACTIVE_LAYER_IDS_ATTR) || ''
  }

  set activeLayerIds(value) {
    if (value !== this.activeLayerIds) {
      if (value) {
        this.setAttribute(ACTIVE_LAYER_IDS_ATTR, value)
      } else {
        this.removeAttribute(ACTIVE_LAYER_IDS_ATTR)
      }
    }
  }
}
